import {
  applyGroupPackageSellDiscount,
  computeGroupPackageOriginalPrice,
} from "@/modules/private-lessons/domain/lib/group-package-pricing";
import {
  useCreateStudent,
  useStudentQuery,
} from "@/modules/students/presentation/api/queries";
import { StudentForm } from "@/modules/students/presentation/ui/StudentForm";
import { config } from "@/shared/lib/config";
import {
  formatDate,
  normalizeSellPackageStartDateValue,
} from "@/shared/lib/date-time";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import type { SellPackageDiscountType } from "@/shared/ui/SellPackageDiscountTypeRadio";
import { SellPackageUsagePeriodStep } from "@/shared/ui/SellPackageUsagePeriodStep";
import type { SellPackageFormValues } from "@/shared/ui/SellPackageForm";
import {
  Alert,
  AlertDescription,
  Button,
  FormRoot,
  FormSelect,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckIcon,
  PackageIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  useCheckActivePrivateLessonPackageQuery,
  usePrivateLessonTemplateQuery,
  usePrivateLessonTemplatesQuery,
} from "../api/queries";
import { mapPrivateLessonFormSessionsToInitialSchedule } from "../lib/map-private-lesson-form-sessions-to-initial-schedule";
import { getSellPlScheduleDefaultsForTemplate } from "../lib/sell-pl-schedule-studio-defaults";
import {
  BulkScheduleFormSchema,
  bulkSessionRowSchema,
} from "./BulkScheduleForm";
import { SellPrivateLessonScheduleOnSellSection } from "./SellPrivateLessonScheduleOnSellSection";

type StudentLookupItem = { id: string; name: string; memberId: string };

const SellPrivateLessonGroupPackageFormSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບກຸ່ມ"),
    ownerStudentId: z.string().min(1, "ຕ້ອງເລືອກຜູ້ຈ່າຍ"),
    memberStudentIds: z
      .array(z.string().min(1))
      .min(1, "ຕ້ອງເລືອກສະມາຊິກຢ່າງໜ້ອຍ 1 ຄົນ"),
    validDays: z.number().int().min(1).max(3650),
    startDate: z.preprocess(normalizeSellPackageStartDateValue, z.date()),
    deactivateExisting: z.boolean().default(false),
    expireDate: z.string().optional(),
    discountType: z.enum(["percentage", "fixed", "none"]).default("none"),
    discountValue: z.number().optional(),
    settlementMode: z.enum(["receipt", "credit"]).default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).default(0),
    plScheduleWithSale: z.boolean().default(false),
    plDefaultRoomId: z.string().default(""),
    plInstructorIds: z.array(z.string()).default([]),
    plSessions: z.array(bulkSessionRowSchema).default([]),
  })
  .superRefine((data, ctx) => {
    const unique = new Set(data.memberStudentIds);
    if (unique.size !== data.memberStudentIds.length) {
      ctx.addIssue({
        code: "custom",
        message: "ສະມາຊິກຊ້ຳກັນ",
        path: ["memberStudentIds"],
      });
    }
    if (!data.memberStudentIds.includes(data.ownerStudentId)) {
      ctx.addIssue({
        code: "custom",
        message: "ຜູ້ຈ່າຍຕ້ອງເປັນຫນຶ່ງໃນສະມາຊິກ",
        path: ["ownerStudentId"],
      });
    }
    if (data.discountType && data.discountType !== "none" && data.discountValue === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "ຕ້ອງກຳນົດຄ່າສ່ວນຫຼຸດ",
        path: ["discountValue"],
      });
    }
    if (
      data.discountValue !== undefined &&
      (!data.discountType || data.discountType === "none")
    ) {
      ctx.addIssue({
        code: "custom",
        message: "ຕ້ອງເລືອກປະເພດສ່ວນຫຼຸດ",
        path: ["discountType"],
      });
    }
  });

export type SellPrivateLessonGroupPackageFormValues = z.infer<
  typeof SellPrivateLessonGroupPackageFormSchema
>;

type SellPrivateLessonGroupPackageFormProps = {
  initialTemplateId?: string;
  initialOwnerStudentId?: string;
  onSubmit: (values: {
    templateId: string;
    ownerStudentId: string;
    memberStudentIds: string[];
    validDays: number;
    startDate: string;
    deactivateExisting: boolean;
    discountType?: "percentage" | "fixed";
    discountValue?: number;
    settlementMode: "receipt" | "credit";
    amountPaidNow: number;
    initialSchedule?: ReturnType<
      typeof mapPrivateLessonFormSessionsToInitialSchedule
    >;
  }) => void;
  submitting?: boolean;
};

export function SellPrivateLessonGroupPackageForm({
  initialTemplateId,
  initialOwnerStudentId,
  onSubmit,
  submitting,
}: SellPrivateLessonGroupPackageFormProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [plScheduleBlocksSubmit, setPlScheduleBlocksSubmit] = useState(false);
  const qc = useQueryClient();
  const createStudent = useCreateStudent();

  const methods = RHF.useForm<SellPrivateLessonGroupPackageFormValues>({
    // @ts-expect-error zod default typing
    resolver: zodResolver(SellPrivateLessonGroupPackageFormSchema),
    defaultValues: {
      templateId: initialTemplateId || "",
      ownerStudentId: initialOwnerStudentId || "",
      memberStudentIds: initialOwnerStudentId ? [initialOwnerStudentId] : [],
      validDays: 30,
      startDate: new Date(),
      deactivateExisting: false,
      discountType: "none",
      settlementMode: "receipt",
      amountPaidNow: 0,
      plScheduleWithSale: false,
      plDefaultRoomId: "",
      plInstructorIds: [],
      plSessions: [],
    },
  });

  const ownerStudentId = methods.watch("ownerStudentId");
  const memberStudentIds = methods.watch("memberStudentIds") ?? [];
  const templateId = methods.watch("templateId");
  const saleValidDays = methods.watch("validDays");
  const startDate = methods.watch("startDate");
  const discountType = methods.watch("discountType");
  const discountValue = methods.watch("discountValue");
  const settlementMode = methods.watch("settlementMode");
  const plScheduleWithSale = methods.watch("plScheduleWithSale");

  const { data: ownerData } = useStudentQuery(ownerStudentId || "");
  const { data: activePackageData } = useCheckActivePrivateLessonPackageQuery(
    ownerStudentId || null,
  );

  const { data: templatesData } = usePrivateLessonTemplatesQuery({
    limit: 100,
    offset: 0,
    filters: [
      { field: "status", op: "eq", value: "active" },
      { field: "packageKind", op: "eq", value: "group" },
    ],
  });

  const { data: templateDetail } = usePrivateLessonTemplateQuery(
    templateId || "",
  );

  const selectedTemplate = useMemo(() => {
    if (!templateId) return null;
    return (
      templateDetail || templatesData?.data.find((t) => t.id === templateId)
    );
  }, [templateId, templateDetail, templatesData]);

  const templateOptions = useMemo(() => {
    return (templatesData?.data ?? []).map((t) => ({
      value: t.id,
      label: `${t.name} (${t.groupSizeMin}–${t.groupSizeMax} ຄົນ, ${t.totalHours} ຊມ., ${(t.flatPrice ?? 0).toLocaleString("lo-LA")} ₭/ຄົນ)`,
    }));
  }, [templatesData]);

  useEffect(() => {
    if (!selectedTemplate) return;
    methods.setValue("validDays", selectedTemplate.validDays, {
      shouldValidate: false,
    });
  }, [templateId, selectedTemplate, methods]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const defaults = getSellPlScheduleDefaultsForTemplate(selectedTemplate);
    for (const [key, value] of Object.entries(defaults)) {
      methods.setValue(
        key as keyof SellPrivateLessonGroupPackageFormValues,
        value as never,
        { shouldValidate: false },
      );
    }
    if (!defaults.plScheduleWithSale) {
      setPlScheduleBlocksSubmit(false);
    }
  }, [selectedTemplate?.id, selectedTemplate?.defaultLocationKind, methods]);

  useEffect(() => {
    if (!ownerStudentId) return;
    const members = methods.getValues("memberStudentIds") ?? [];
    if (!members.includes(ownerStudentId)) {
      methods.setValue("memberStudentIds", [...members, ownerStudentId], {
        shouldValidate: true,
      });
    }
  }, [ownerStudentId, methods]);

  useEffect(() => {
    if (selectedTemplate && startDate) {
      try {
        const startDateObj =
          startDate instanceof Date ? startDate : new Date(startDate);
        const days =
          saleValidDays != null && saleValidDays >= 1
            ? saleValidDays
            : selectedTemplate.validDays;
        methods.setValue(
          "expireDate",
          format(addDays(startDateObj, days), "yyyy-MM-dd"),
          {
            shouldValidate: false,
          },
        );
      } catch {
        //
      }
    }
  }, [selectedTemplate, startDate, saleValidDays, methods]);

  const memberCount = memberStudentIds.length;
  const sizeInRange =
    selectedTemplate == null ||
    (selectedTemplate.groupSizeMin != null &&
      selectedTemplate.groupSizeMax != null &&
      memberCount >= selectedTemplate.groupSizeMin &&
      memberCount <= selectedTemplate.groupSizeMax);

  const priceCalculation = useMemo(() => {
    if (!selectedTemplate?.flatPrice || memberCount < 1) {
      return {
        originalPrice: 0,
        discountAmount: 0,
        finalPrice: 0,
        pricePerPerson: 0,
      };
    }
    const pricePerPerson = selectedTemplate.flatPrice;
    const originalPrice = computeGroupPackageOriginalPrice(
      pricePerPerson,
      memberCount,
    );
    const { discountAmount, finalPrice } = applyGroupPackageSellDiscount(
      originalPrice,
      discountType && discountType !== "none" ? discountType : undefined,
      discountValue,
    );
    return { originalPrice, discountAmount, finalPrice, pricePerPerson };
  }, [selectedTemplate, memberCount, discountType, discountValue]);

  const expireDate = methods.watch("expireDate");
  const usageStepNum = selectedTemplate ? 5 : 3;
  const scheduleMethods = methods as unknown as UseFormReturn<
    SellPackageFormValues,
    unknown,
    unknown
  >;

  const handleSubmit = (values: SellPrivateLessonGroupPackageFormValues) => {
    if (values.plScheduleWithSale && plScheduleBlocksSubmit) return;

    const fp = priceCalculation.finalPrice;
    if (values.settlementMode === "credit" && fp > 0) {
      const apn = values.amountPaidNow ?? 0;
      if (apn < 0 || apn > fp) {
        methods.setError("amountPaidNow", {
          message: `ຕ້ອງຢູ່ລະຫວ່າງ 0 ແລະ ${fp.toLocaleString("lo-LA")} ກີບ`,
        });
        return;
      }
    }

    const startDateValue =
      values.startDate instanceof Date
        ? values.startDate
        : new Date(values.startDate);

    let initialSchedule:
      | ReturnType<typeof mapPrivateLessonFormSessionsToInitialSchedule>
      | undefined;
    if (values.plScheduleWithSale) {
      const parsed = BulkScheduleFormSchema.safeParse({
        defaultRoomId: values.plDefaultRoomId,
        instructorIds: values.plInstructorIds ?? [],
        sessions: values.plSessions ?? [],
      });
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message ?? "ກຳນົດຕາຕະລາງບໍ່ຖືກຕ້ອງ");
        return;
      }
      try {
        initialSchedule = mapPrivateLessonFormSessionsToInitialSchedule(
          parsed.data.instructorIds,
          parsed.data.sessions,
        );
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ກຳນົດຕາຕະລາງບໍ່ຖືກຕ້ອງ");
        return;
      }
    }

    onSubmit({
      templateId: values.templateId,
      ownerStudentId: values.ownerStudentId,
      memberStudentIds: values.memberStudentIds,
      validDays: values.validDays,
      startDate: format(startDateValue, "yyyy-MM-dd"),
      deactivateExisting: values.deactivateExisting,
      discountType:
        values.discountType && values.discountType !== "none"
          ? values.discountType
          : undefined,
      discountValue: values.discountValue,
      settlementMode: values.settlementMode,
      amountPaidNow:
        values.settlementMode === "credit"
          ? Math.min(Math.max(0, values.amountPaidNow ?? 0), fp)
          : 0,
      initialSchedule,
    });
  };

  const handleCreateStudent = async (
    values: Parameters<typeof createStudent.mutateAsync>[0],
  ) => {
    const result = await createStudent.mutateAsync(values);
    await hydrateLookupItem<StudentLookupItem>(
      `${config.apiUrl}/students/lookup`,
      result.id,
    );
    qc.invalidateQueries({ queryKey: ["students", "lookup"] });
    methods.setValue("ownerStudentId", result.id, { shouldValidate: true });
    setIsCreateModalOpen(false);
  };

  return (
    // @ts-expect-error FormRoot typing
    <FormRoot methods={methods} onSubmit={handleSubmit}>
      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                1
              </div>
              <h3 className="font-semibold text-lg">ເລືອກແມ່ແບບກຸ່ມ</h3>
            </div>
            <FormSelect
              name="templateId"
              label="ແມ່ແບບແພັກເກດກຸ່ມ"
              requiredMark
              options={templateOptions}
              placeholder="ເລືອກ tier ຈຳນວນຄົນ..."
            />
            {selectedTemplate && (
              <div className="mt-4 rounded-lg border bg-muted/40 p-4 text-sm">
                <p className="font-semibold">{selectedTemplate.name}</p>
                <p className="text-muted-foreground">
                  {selectedTemplate.totalHours} ຊົ່ວໂມງ ·{" "}
                  {selectedTemplate.groupSizeMin}–
                  {selectedTemplate.groupSizeMax} ຄົນ ·{" "}
                  {(selectedTemplate.flatPrice ?? 0).toLocaleString("lo-LA")} ₭
                  ຕໍ່ຄົນ
                </p>
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                2
              </div>
              <h3 className="font-semibold text-lg">ສະມາຊິກ ແລະ ຜູ້ຈ່າຍ</h3>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col items-end gap-2 md:flex-row">
                <FormInfiniteCombobox<StudentLookupItem>
                  name="ownerStudentId"
                  label="ຜູ້ຈ່າຍ / ເຈົ້າຂອງແພັກ"
                  requiredMark
                  queryKey={["students", "lookup"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite<StudentLookupItem>(
                      `${config.apiUrl}/students/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem<StudentLookupItem>(
                      `${config.apiUrl}/students/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) => `${item.memberId} - ${item.name}`}
                  getValue={(item) => item.id}
                  placeholder="ຄົ້ນຫານັກຮຽນ..."
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <UserPlusIcon className="mr-2 size-4" />
                  ນັກຮຽນໃໝ່
                </Button>
              </div>

              <FormInfiniteMultiCombobox<StudentLookupItem>
                name="memberStudentIds"
                label="ສະມາຊິກໃນກຸ່ມ"
                requiredMark
                hint={
                  selectedTemplate
                    ? `ຕ້ອງ ${selectedTemplate.groupSizeMin}–${selectedTemplate.groupSizeMax} ຄົນ (ປັດຈຸບັນ ${memberCount})`
                    : undefined
                }
                queryKey={["students", "lookup", "group-members"]}
                queryFn={(args) =>
                  fetchLookupForInfinite<StudentLookupItem>(
                    `${config.apiUrl}/students/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(ids) =>
                  hydrateLookupItems<StudentLookupItem>(
                    `${config.apiUrl}/students/lookup`,
                    ids,
                  )
                }
                getLabel={(item) => `${item.memberId} - ${item.name}`}
                getValue={(item) => item.id}
                placeholder="ເລືອກສະມາຊິກຫຼາຍຄົນ..."
              />

              {!sizeInRange && selectedTemplate && (
                <Alert variant="destructive">
                  <AlertTriangleIcon className="size-4" />
                  <AlertDescription>
                    ຈຳນວນສະມາຊິກຕ້ອງຢູ່ລະຫວ່າງ {selectedTemplate.groupSizeMin}–
                    {selectedTemplate.groupSizeMax} ຄົນ
                  </AlertDescription>
                </Alert>
              )}

              {activePackageData?.hasActivePackage && (
                <Alert>
                  <AlertTriangleIcon className="size-4" />
                  <AlertDescription>
                    ເຈົ້າຂອງມີແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ງານຢູ່ — ສາມາດປິດກ່ອນຂາຍໃໝ່ໄດ້
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {selectedTemplate && (
            <SellPrivateLessonScheduleOnSellSection
              methods={scheduleMethods}
              purchasedHours={selectedTemplate.totalHours}
              templateDefaultLocationKind={selectedTemplate.defaultLocationKind}
              onScheduleBlocksSubmit={setPlScheduleBlocksSubmit}
            />
          )}

          <SellPackageUsagePeriodStep
            stepNumber={usageStepNum}
            control={methods.control}
            discountType={discountType as SellPackageDiscountType | undefined}
            settlementMode={settlementMode}
            expireDate={expireDate}
            validDaysBadge={saleValidDays}
            priceSummary={selectedTemplate ? priceCalculation : null}
            onDiscountTypeChange={(next) => {
              if (next === "none") {
                methods.setValue("discountValue", undefined, {
                  shouldValidate: false,
                });
              }
            }}
          />
        </div>

        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-xl border border-l-4 border-l-red-600 bg-card p-6 shadow-lg">
            <h3 className="mb-4 font-semibold text-lg">ສະຫຼຸບຄຳສັ່ງຊື້</h3>
            <div className="space-y-4">
              {ownerData && (
                <div className="flex gap-3">
                  <UserIcon className="mt-1 size-4 text-red-600" />
                  <div>
                    <p className="text-muted-foreground text-xs">ຜູ້ຈ່າຍ</p>
                    <p className="font-semibold">{ownerData.name}</p>
                  </div>
                </div>
              )}
              {selectedTemplate && (
                <div className="flex gap-3">
                  <PackageIcon className="mt-1 size-4 text-red-600" />
                  <div>
                    <p className="text-muted-foreground text-xs">ແພັກເກດກຸ່ມ</p>
                    <p className="font-semibold">{selectedTemplate.name}</p>
                    <p className="flex items-center gap-1 text-muted-foreground text-sm">
                      <UsersIcon className="size-3.5" />
                      {memberCount} ຄົນ · {selectedTemplate.totalHours} ຊົ່ວໂມງ
                    </p>
                  </div>
                </div>
              )}
              {startDate && expireDate && (
                <div className="flex gap-3">
                  <CalendarIcon className="mt-1 size-4 text-red-600" />
                  <p className="text-sm">
                    {formatDate(startDate)}{" "}
                    <ArrowRightIcon className="inline size-3" />{" "}
                    {formatDate(expireDate)}
                  </p>
                </div>
              )}
              <div className="border-t pt-4">
                {priceCalculation.pricePerPerson > 0 && memberCount > 0 && (
                  <p className="mb-1 text-muted-foreground text-sm">
                    {priceCalculation.pricePerPerson.toLocaleString("lo-LA")} ₭
                    × {memberCount} ຄົນ
                    {priceCalculation.discountAmount > 0 && (
                      <>
                        {" "}
                        (−
                        {priceCalculation.discountAmount.toLocaleString(
                          "lo-LA",
                        )}{" "}
                        ₭)
                      </>
                    )}
                  </p>
                )}
                <p className="font-bold text-2xl text-red-600">
                  {priceCalculation.finalPrice.toLocaleString("lo-LA")} ₭
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={
                  submitting ||
                  !sizeInRange ||
                  (plScheduleWithSale && plScheduleBlocksSubmit)
                }
              >
                <CheckIcon className="mr-2 size-4" />
                {submitting ? "ກຳລັງຂາຍ..." : "ຢືນຢັນແລະຂາຍແພັກເກດກຸ່ມ"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        title="ລົງທະບຽນນັກຮຽນໃໝ່"
        size="md"
        className="max-h-[85vh] overflow-y-auto"
      >
        <StudentForm
          onSubmit={handleCreateStudent}
          submitting={createStudent.isPending}
        />
      </Modal>
    </FormRoot>
  );
}
